import {
  Button,
  ButtonMode,
  Checkbox,
  DataLoadError,
  Divider,
  Icon,
  ICONS,
  Loader,
  Tooltip,
} from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Pluralize from 'react-pluralize';
import { useOutletContext, useParams } from 'react-router-dom';
import { GENERIC_ERROR_TEXT } from '../../../../Base.constants';
import { CheckboxChangeEvent } from '../../../../components/formatters/CheckboxCellRenderer/CheckboxCellRenderer.types';
import { useGlobalModalContext } from '../../../../components/GlobalModal';
import { MODAL_TYPES } from '../../../../components/GlobalModal/GlobalModal';
import { useFetchInfiniteIssuers } from '../../../../hooks/issuers';
import { useToast } from '../../../../hooks/toast/useToast';
import { IssuerListItemDto } from '../../../../services/dtos/issuer/issuerList.dto';
import { formatCommonDate } from '../../../../utils/dateUtils';
import { isNotFound } from '../../../../utils/navigationUtils';
import NotFound from '../../../NotFound';
import NoContent from '../../NoContent/NoContent';
import WatchlistName from '../WatchlistName/WatchlistName';
import { WatchlistsOutletContext } from '../WatchlistsTab';
import { isNewWatchlist } from '../WatchlistsTab.logic';
import WatchlistTable from '../WatchlistTable/WatchlistTable';
import AlertPopup from './AlertPopup/AlertPopup';
import {
  BOTH_LIMITS_EXCEEDED_MESSAGE,
  getIsUserLimitExceeded,
  getIsWatchlistLimitExceeded,
  USER_LIMIT_EXCEEDED_MESSAGE,
  WATCHLIST_LIMIT_EXCEEDED_MESSAGE,
} from './Watchlist.logic';
import styles from './Watchlist.module.scss';

const cx = classNames.bind(styles);

const getAddIssuersTooltip = (isWatchlistLimitExceeded: boolean, isUserLimitExceeded: boolean): string => {
  if (isWatchlistLimitExceeded && !isUserLimitExceeded) {
    return WATCHLIST_LIMIT_EXCEEDED_MESSAGE;
  }
  if (!isWatchlistLimitExceeded && isUserLimitExceeded) {
    return USER_LIMIT_EXCEEDED_MESSAGE;
  }
  return BOTH_LIMITS_EXCEEDED_MESSAGE;
};

const Watchlist = (): React.ReactElement | null => {
  const [issuersToRemove, setIssuersToRemove] = useState<string[]>([]);
  const [isAlertPopupOpen, setIsAlertPopupOpen] = useState<boolean>(false);
  const [isNameBeingChanged, setIsNameBeingChanged] = useState<boolean>(false);
  const { showModal, closeModal, setError } = useGlobalModalContext();
  const { id = '' } = useParams<string>();
  const {
    data: issuers,
    isLoading: isIssuersLoading,
    isError,
    error,
    isRefetching: isRefetchingIssuers,
    refetch,
    isFetchingNextPage,
    hasNextPage,
    totalResults,
    fetchNextPage,
  } = useFetchInfiniteIssuers({
    watchlistId: id,
    onlyActive: false,
    take: 150,
  });
  const {
    watchlists,
    newEmptyWatchlist,
    onWatchlistUpdate,
    isLoading: isAnyWatchlistLoading,
    isWatchlistNameChanging,
  } = useOutletContext<WatchlistsOutletContext>();
  const { showToast } = useToast();

  const issuerTagsIds = useMemo<string[]>(() => issuers?.map((issuer) => issuer.tagId) ?? [], [issuers]);

  const openedWatchlist = watchlists?.find((watchlist) =>
    isNewWatchlist(id) ? watchlist.id === newEmptyWatchlist?.id : watchlist.id === id
  );

  const watchlistsNames = watchlists
    ?.filter((watchlist) => watchlist.id !== openedWatchlist?.id && watchlist)
    .map((watchlist) => watchlist.name);

  const isWatchlistLimitExceeded = getIsWatchlistLimitExceeded(openedWatchlist);
  const isUserLimitExceeded = getIsUserLimitExceeded(watchlists);
  const isAddIssuersDisabled = isWatchlistLimitExceeded || isUserLimitExceeded;

  const onIssuerSelected = ({ checked, indeterminate, item }: CheckboxChangeEvent) => {
    if (!issuers) {
      return;
    }
    const issuer = item as IssuerListItemDto;
    let tagsIdsToRemove: string[];
    if (indeterminate) {
      tagsIdsToRemove = issuerTagsIds;
    } else if (checked) {
      tagsIdsToRemove = issuer ? [...issuersToRemove, issuer.tagId] : issuerTagsIds;
    } else {
      tagsIdsToRemove = issuer ? issuersToRemove.filter((issuerToRemove) => issuerToRemove !== issuer.tagId) : [];
    }

    setIssuersToRemove(tagsIdsToRemove);
  };

  const onRemoveSuccess = () => {
    closeModal();
    setIssuersToRemove([]);
    showToast(issuersToRemove.length > 1 ? 'Your issuers have been removed' : 'Your issuer has been removed');
  };

  const onRemoveIssuers = useCallback(() => {
    showModal(MODAL_TYPES.CONFIRM_REMOVE_ISSUERS, {
      isMultipleIssuers: issuersToRemove.length > 1,
      onConfirm: () => {
        if (!openedWatchlist) {
          return;
        }
        onWatchlistUpdate(
          {
            id: openedWatchlist.id,
            watchList: {
              tags: openedWatchlist.tags.filter((tag) => !issuersToRemove.includes(tag)),
            },
          },
          {
            onSuccess: onRemoveSuccess,
            onError: () => setError(GENERIC_ERROR_TEXT),
          }
        );
      },
    });
  }, [onWatchlistUpdate, showModal, issuersToRemove, openedWatchlist]);

  const onAddIssuers = () => {
    showModal(MODAL_TYPES.ADD_ISSUERS);
  };

  const toggleMyFeed = (_, data) => {
    if (openedWatchlist) {
      onWatchlistUpdate({
        id: openedWatchlist.id,
        watchList: {
          myCS: data.checked,
        },
      });
    }
  };

  const selectAlert = (alert) => {
    if (openedWatchlist) {
      onWatchlistUpdate({
        id: openedWatchlist.id,
        watchList: {
          mode: alert,
        },
      });
    }

    setIsAlertPopupOpen(false);
  };

  useEffect(() => {
    setIssuersToRemove([]);
  }, [id]);

  useEffect(() => {
    setIsNameBeingChanged(isWatchlistNameChanging);
  }, [isWatchlistNameChanging]);

  const removeCount = issuersToRemove.length > 0 ? `(${issuersToRemove.length})` : '';

  const renderWatchlist = () => {
    // 400 is returned in case invalid watchlist id was provided in the url, therefore there's no point retrying it.
    // Instead, show page not found
    if (isError && !isNotFound(error)) {
      return <DataLoadError onRetryClick={refetch} />;
    }

    if (isIssuersLoading || isAnyWatchlistLoading || (isRefetchingIssuers && !isFetchingNextPage)) {
      return <Loader position="center" />;
    }

    if (!openedWatchlist) {
      return <NotFound showNavigateToFeeds={false} />;
    }

    return (
      <>
        <header className={cx('header')}>
          <WatchlistName
            watchlist={openedWatchlist}
            watchlistsNames={watchlistsNames}
            isNewWatchlist={isNewWatchlist(openedWatchlist.id)}
            onActiveChanged={setIsNameBeingChanged}
          />
          <div className={cx('subheader')}>
            <div className={cx('summary')}>
              <div className={cx('summary-item')} data-testid="watchlist-issuers-count">
                <Pluralize count={totalResults || 0} singular="Issuer" plural="Issuers" />
              </div>
              <div className={cx('summary-item')}>Last Edited {formatCommonDate(openedWatchlist.updatedAt, true)}</div>
            </div>
            <div className={cx('toolbar')}>
              <div className={cx('toolbar-section')}>
                <Tooltip
                  disabled={!!issuersToRemove.length || isNameBeingChanged}
                  content="Select issuer(s) to remove"
                  data-testid="remove-issuers-tooltip"
                >
                  <div>
                    <Button
                      ariaLabel={`Remove Selected ${removeCount}`}
                      disabled={!issuersToRemove.length || isNameBeingChanged}
                      mode={ButtonMode.TERTIARY}
                      onClick={onRemoveIssuers}
                    >
                      <Icon name={ICONS.Delete} />
                      Remove Selected {removeCount}
                    </Button>
                  </div>
                </Tooltip>
                <Tooltip
                  content={getAddIssuersTooltip(isWatchlistLimitExceeded, isUserLimitExceeded)}
                  disabled={!isAddIssuersDisabled}
                >
                  <div>
                    <Button
                      ariaLabel="Add Issuer"
                      mode={ButtonMode.TERTIARY}
                      onClick={onAddIssuers}
                      disabled={isAddIssuersDisabled || isNameBeingChanged}
                    >
                      <Icon name={ICONS.Plus} height={12} width={12} />
                      Add Issuer
                    </Button>
                  </div>
                </Tooltip>
              </div>
              <div className={cx('toolbar-section')}>
                <Divider vertical />
                <Checkbox
                  className={cx('my-feed-toggle')}
                  dataTestId="my-feed-toggle"
                  onClick={toggleMyFeed}
                  readOnly={isNameBeingChanged}
                  disabled={isNameBeingChanged}
                  checked={openedWatchlist.myCS}
                  label="My Feed"
                  aria-label="My Feed"
                  toggle
                />
                <AlertPopup
                  alert={openedWatchlist.mode}
                  selectAlert={selectAlert}
                  isOpen={isAlertPopupOpen}
                  setIsOpen={setIsAlertPopupOpen}
                  isDisabled={isNameBeingChanged}
                />
              </div>
            </div>
          </div>
        </header>
        <div className={cx('content')}>
          <WatchlistTable
            issuers={issuers}
            isIssuersLoading={isIssuersLoading}
            isFetchingNextPage={isFetchingNextPage}
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
            onIssuerSelected={onIssuerSelected}
            issuersToRemove={issuersToRemove}
          />
          {issuers?.length === 0 && (
            <NoContent
              dataTestId="no-issuers-added"
              header="No Issuers Yet"
              infoMessage="When you add issuers to this watchlist, they will appear here."
              buttonParams={{
                ariaLabel: 'Add Issuer',
                title: 'Add Issuer',
                disabled: isUserLimitExceeded || isNameBeingChanged,
                iconName: ICONS.Plus,
                onClick: onAddIssuers,
                tooltip: {
                  content: USER_LIMIT_EXCEEDED_MESSAGE,
                  disabled: !isUserLimitExceeded,
                },
              }}
            />
          )}
        </div>
      </>
    );
  };

  return <div className={cx('body')}>{renderWatchlist()}</div>;
};

export default Watchlist;
